import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { 
  LayoutDashboard, 
  Search, 
  Users, 
  FileText, 
  Settings,
  UserCog,
  ShieldCheck,
  LogOut,
  ChevronRight
} from 'lucide-react'

const Sidebar = () => {
  const location = useLocation()
  const { user, logout } = useAuth()

  const getRoleLabel = (role) => ({
    cmo: 'Insurance Manager',
    super_admin: 'Insurance Admin',
    agent: 'Insurance Executive',
  }[role] || role?.replace('_', ' ') || 'Role')
  
  const isActive = (path) => location.pathname.startsWith(path)
  
  // Define navigation items based on user role
  const getNavItems = () => {
    const commonItems = [
      { path: '/patients/search', icon: Search, label: 'Search Patient' },
      { path: '/patients/list', icon: Users, label: 'Patient Records' },
    ]
    
    const adminItems = user?.role === 'super_admin' ? [
      { path: '/admin', icon: UserCog, label: 'Generated Final Bill Patients', divider: true },
      { path: '/admin/users', icon: Settings, label: 'User Management' },
    ] : []

    const cmoItems = user?.role === 'cmo' ? [
      { path: '/cmo', icon: ShieldCheck, label: 'Generated Final Bill Patients', divider: true },
    ] : []
    
    return [...commonItems, ...adminItems, ...cmoItems]
  }
  
  const navItems = getNavItems()
  
  return (
    <div className="w-64 bg-gradient-to-b from-blue-900 to-blue-800 text-white flex flex-col shadow-xl">
      {/* Logo/Brand */}
      <div className="p-6 border-b border-blue-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center p-1">
            <img src="/favicon.ico" alt="Epione Logo" className="w-full h-full object-contain" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Epione</h1>
            <p className="text-xs text-blue-200">Portal</p>
          </div>
        </div>
      </div>
      
      {/* User Info */}
      <div className="p-4 bg-blue-800/50 border-b border-blue-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center font-semibold">
            {user?.username?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.username || 'User'}</p>
            <p className="text-xs text-blue-200">{getRoleLabel(user?.role)}</p>
          </div>
        </div>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {navItems.map((item, index) => (
          <div key={item.path}>
            {item.divider && <div className="my-4 border-t border-blue-700" />}
            <Link
              to={item.path}
              className={`flex items-center px-6 py-3 transition-all duration-200 group relative ${
                isActive(item.path)
                  ? 'bg-blue-700 text-white shadow-lg'
                  : 'text-blue-100 hover:bg-blue-700/50 hover:text-white'
              }`}
            >
              {isActive(item.path) && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-white rounded-r" />
              )}
              <item.icon size={20} className="mr-3" />
              <span className="flex-1 font-medium">{item.label}</span>
              {isActive(item.path) && <ChevronRight size={16} />}
            </Link>
          </div>
        ))}
      </nav>
      
      {/* Logout Button */}
      <div className="p-4 border-t border-blue-700">
        <button
          onClick={logout}
          className="w-full flex items-center px-4 py-3 text-blue-100 hover:bg-red-600 hover:text-white rounded-lg transition-all duration-200 group"
        >
          <LogOut size={20} className="mr-3" />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </div>
  )
}

export default Sidebar
